import * as React from "react";

import { rgba } from "polished";

import {
  Module1,
  Module2,
  Module3,
  Module4,
  Start,
  Registration,
  ProgramFinish,
} from "./views";

import { useGlobalUser, AppContextProvider } from "./state";
import { TOTAL_MODULES } from "./constants";

export const App: React.FC = () => {
  const [
    userState,
    userSetState,
    { reset, finish, goToModule },
  ] = useGlobalUser();

  const wrapperRef = React.useRef<HTMLDivElement>(null);

  const [state, setState] = React.useState(() => {
    return {
      showResumeModal: userState.email !== "" && userState.view !== "start",
    };
  });

  const createModuleChangeHandler = (moduleNumber: number) => (
    e: React.MouseEvent<HTMLButtonElement>,
  ) => {
    e.preventDefault();
    userSetState((prev) => ({
      ...prev,
      currentModule: moduleNumber,
      currentQuestion: 0,
      view: "module",
      startedModules: {
        ...prev.startedModules,
        [moduleNumber]: true,
      },
    }));
  };

  const latestModule = React.useMemo(() => {
    return Object.keys(userState.startedModules).length;
  }, []);

  const hasUserFinished = React.useMemo(() => {
    return Object.entries(userState.completedModules).length === TOTAL_MODULES;
  }, []);

  return (
    <AppContextProvider wrapperRef={wrapperRef}>
      <div
        ref={wrapperRef}
        css={`
          position: relative;
        `}
      >
        {state.showResumeModal && (
          <div
            css={`
              position: absolute;
              top: -2rem;
              left: -2rem;
              right: -2rem;
              bottom: -2rem;
              background: ${(p: any) => rgba(p.theme.info, 0.9)};
              z-index: 99999;
            `}
          >
            <div
              css={`
                position: sticky;
                top: 5rem;
                bottom: 0;
              `}
              className="modal-dialog"
              role="document"
            >
              <div className="modal-content">
                <div className="modal-body py-4">
                  <h1 className="text-center text-warning">
                    Hi {userState.firstName}
                  </h1>
                  {hasUserFinished ? (
                    <p className="text-center">
                      We noticed you have already completed this training
                      course. Please choose how you would like to proceed.
                    </p>
                  ) : (
                    <p className="text-center">
                      We noticed you have already begun this training course and
                      are up to module {latestModule}. Please choose how you
                      would like to proceed.
                    </p>
                  )}

                  <div className="w-75 mx-auto">
                    {hasUserFinished ? (
                      <button
                        className="btn btn-info btn-block"
                        onClick={(e) => {
                          e.preventDefault();
                          setState((prev) => ({
                            ...prev,
                            showResumeModal: false,
                          }));
                          finish();
                        }}
                      >
                        Go to completion page
                      </button>
                    ) : (
                      <button
                        className="btn btn-info btn-block"
                        onClick={(e) => {
                          e.preventDefault();
                          setState((prev) => ({
                            ...prev,
                            showResumeModal: false,
                          }));
                          goToModule(latestModule);
                        }}
                      >
                        Continue from module {latestModule}
                      </button>
                    )}

                    <button
                      className="btn btn-info btn-block"
                      onClick={(e) => {
                        setState((prev) => ({
                          ...prev,
                          showResumeModal: false,
                        }));
                        reset();
                      }}
                    >
                      Start again
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main app */}
        {process.env.NODE_ENV !== "production" && (
          <div
            css={`
              margin-bottom: 2rem;
            `}
          >
            <button
              onClick={(e) => {
                e.preventDefault();
                userSetState((prev) => ({
                  ...prev,
                  currentModule: 0,
                  currentQuestion: 0,
                  view: "start",
                }));
              }}
            >
              Start
            </button>
            <button
              onClick={(e) => {
                e.preventDefault();
                userSetState(
                  (prev) => ({
                    ...prev,
                    currentModule: 0,
                    currentQuestion: 0,
                    view: "registration",
                  }),
                  false,
                );
              }}
            >
              Registration
            </button>
            <button onClick={createModuleChangeHandler(1)}>Module 1</button>
            <button onClick={createModuleChangeHandler(2)}>Module 2</button>
            <button onClick={createModuleChangeHandler(3)}>Module 3</button>
            <button onClick={createModuleChangeHandler(4)}>Module 4</button>
            <button
              onClick={(e) => {
                e.preventDefault();
                finish();
              }}
            >
              Finish
            </button>
          </div>
        )}
        <div>
          {(() => {
            switch (userState.view) {
              case "start":
                return <Start />;
              case "registration":
                return <Registration />;
              case "module":
                return (() => {
                  switch (userState.currentModule) {
                    case 0:
                      throw Error(
                        "No module step 0, did you mean Module 1? Set userState Module to 1.",
                      );
                    case 1:
                      return <Module1 />;
                    case 2:
                      return <Module2 />;
                    case 3:
                      return <Module3 />;
                    case 4:
                      return <Module4 />;
                    default:
                      throw Error(
                        `No module step for ${userState.currentModule}`,
                      );
                  }
                })();
              case "end":
                return <ProgramFinish />;
              default:
                return null;
            }
          })()}
        </div>
      </div>
    </AppContextProvider>
  );
};
